// src/logbook.js

import React, { useMemo } from "react";

const Logbook = ({ hero, program, logForHero }) => {
  // logForHero shape:
  // {
  //   [weekNumber]: {
  //     [dayId]: {
  //       [exerciseName]: {
  //         rpe: string,
  //         sets: [{ weight, reps, rpe }]
  //       }
  //     }
  //   }
  // }

  const daysById = useMemo(() => {
    const map = {};
    (program.days || []).forEach((d) => {
      map[d.id] = d;
    });
    return map;
  }, [program.days]);

  const weekEntries = useMemo(() => {
    const entries = Object.entries(logForHero || {});
    // sort by numeric week
    return entries
      .map(([weekKey, data]) => [Number(weekKey), data])
      .sort((a, b) => a[0] - b[0]);
  }, [logForHero]);

  const hasAnyLogs = weekEntries.length > 0;

  const handleExportPdf = () => {
    if (typeof window === "undefined") return;

    const printContainer = document.getElementById("hero-logbook-print");
    if (!printContainer) return;

    const printWindow = window.open("", "", "height=900,width=700");
    if (!printWindow) return;

    // Grab existing styles (so the PDF looks like the app)
    const styles = Array.from(
      document.querySelectorAll("style, link[rel='stylesheet']")
    )
      .map((node) => node.outerHTML)
      .join("");

    printWindow.document.write(
      "<html><head><title>Hero Physique Logbook</title>"
    );
    printWindow.document.write(styles);
    // Hide export buttons inside the print view
    printWindow.document.write(
      "<style>@media print {.export-btn{display:none !important;}}</style>"
    );
    printWindow.document.write("</head><body class='app'>");

    // Optional header for the PDF
    printWindow.document.write(`
      <div style="padding: 16px 16px 8px 16px;">
        <h2 style="margin: 0 0 4px 0; font-size: 1.4rem;">Hero Physique Logbook</h2>
        <div style="font-size: 0.9rem; opacity: 0.85;">
          ${hero.name} • ${program.name}
        </div>
      </div>
    `);

    // Inject the logbook content
    printWindow.document.write(
      `<div style="padding: 0 16px 24px 16px;">${printContainer.innerHTML}</div>`
    );

    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.focus();

    // Trigger print → user can choose "Save as PDF"
    printWindow.print();
    printWindow.close();
  };

  if (!hasAnyLogs) {
    return (
      <div className="card">
        <div className="card-header">
          <div className="card-title">Logbook</div>
          <div className="tag">No entries yet</div>
        </div>
        <p className="program-desc">
          Your detailed set-by-set history will appear here as you log workouts
          in the <strong>Workouts</strong> tab.
          <br />
          <br />
          Use this section to review past weeks, spot trends in weight and reps,
          and see how your hero physique is progressing over time.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header" style={{ alignItems: "center" }}>
        <div>
          <div className="card-title">Logbook</div>
          <div className="tag">
            {hero.name} • {program.name}
          </div>
        </div>
        <button
          className="export-btn"
          onClick={handleExportPdf}
          style={{
            marginLeft: "auto",
            padding: "6px 10px",
            borderRadius: 999,
            border: "1px solid rgba(56,189,248,0.8)",
            background:
              "radial-gradient(circle at 20% 0%, rgba(56,189,248,0.35), rgba(15,23,42,0.98))",
            color: "#e0f2fe",
            fontSize: "0.8rem",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Export as PDF
        </button>
      </div>

      <div id="hero-logbook-print">
        <p className="program-desc" style={{ marginBottom: 12 }}>
          Review your logged sets by week, day, and exercise. Use this to decide
          when to add weight, reps, or sets as you move through the 9–12 week
          block.
        </p>

        <div className="logbook-list">
          {weekEntries.map(([weekNumber, weekData]) => {
            const dayEntries = Object.entries(weekData || {});
            if (!dayEntries.length) return null;

            return (
              <div key={weekNumber} className="logbook-week">
                <div className="section-label">Week {weekNumber}</div>

                {dayEntries.map(([dayId, dayLog]) => {
                  const day = daysById[dayId];
                  const dayLabel = day ? day.label : `Day ${dayId}`;
                  const exerciseEntries = Object.entries(dayLog || {});
                  if (!exerciseEntries.length) return null;

                  return (
                    <div key={dayId} className="logbook-day">
                      <div className="logbook-day-header">
                        <span className="badge">{dayLabel}</span>
                      </div>

                      <div className="logbook-exercise-list">
                        {exerciseEntries.map(([exerciseName, entry]) => {
                          const sets = Array.isArray(entry.sets)
                            ? entry.sets
                            : [];
                          return (
                            <div
                              key={exerciseName}
                              className="logbook-exercise"
                            >
                              <div className="exercise-name">
                                {exerciseName}
                              </div>
                              <div className="logbook-sets">
                                {sets.length === 0 && (
                                  <div className="exercise-notes">
                                    No sets logged.
                                  </div>
                                )}
                                {sets.map((s, idx) => (
                                  <div key={idx} className="logbook-set-row">
                                    <span className="set-label">
                                      Set {idx + 1}:
                                    </span>
                                    <span className="set-value">
                                      {s.weight || "-"} lbs × {s.reps || "-"}{" "}
                                      reps
                                    </span>
                                    {s.rpe && (
                                      <span className="set-rpe">
                                        • RPE {s.rpe}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Logbook;
